import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Map } from 'immutable'

import ADND from '@aytm/lib/adnd/ADND'

import ContextSubscriber from '@aytm/lib/common/components/decorators/ContextSubscriber'
import ContextProvider from '@aytm/lib/common/components/decorators/ContextProvider'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'

import { reportStatePath, updateListState } from '@aytm/app/stats/page/sidebar/reports/actions'

import { isSkipperGuidedReport } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import { SKIPPER_REPORT_FEATURE } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'
import { decodeHtmlEntities } from '@aytm/app/stats/lib/decode-html-entities'

// components
import ReportPopup from '@aytm/app/stats/page/sidebar/reports/ReportPopup'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/Report.module.scss'

// A drag source pairs only with a target of the same type, so the type is what parts the
// list into pools a row may be reordered within: guided Skipper rows, the grouped types,
// and everything else, each split again by whether the row is hidden.
const dndType = ({ model, windowUID, virtualReportsIds }) => {
  const type = model.get('type')
  const group = isSkipperGuidedReport(model)
    ? `-${type}-guided`
    : ['skipper', 'concept', ...virtualReportsIds].includes(type)
    ? `-${type}`
    : ''

  return `report-${windowUID}${group}${model.get('hidden') ? '-hidden' : ''}`
}

// <Component>
// eslint-disable-next-line no-class-assign
class Report extends ManagedRenderComponent {
  static propTypes = {
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    position: PropTypes.number.isRequired,
    dndItemsList: PropTypes.object,
    saveReportTypeRef: PropTypes.func,
    saveReportPositionRef: PropTypes.func
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.position != prevProps.position ||
      nextProps.currentReportId != prevProps.currentReportId ||
      nextProps.disabled != prevProps.disabled
    )
  }

  state = {
    overflow: false
  }

  constructor(props, context) {
    super(props, context)

    this.saveListItemRef = this.saveListItemRef.bind(this)
    this.saveListItemHandleRef = this.saveListItemHandleRef.bind(this)
    this.connectDNDListItem = this.connectDNDListItem.bind(this)
    this.connectDNDListItemHandle = this.connectDNDListItemHandle.bind(this)
    this.onReportDelete = this.onReportDelete.bind(this)
    this.onReportSelect = this.onReportSelect.bind(this)
    this.onReportView = this.onReportView.bind(this)
    this.checkOverflow = this.checkOverflow.bind(this)
    this.renderTitle = this.renderTitle.bind(this)

    const type = dndType(props)

    this.dndListItem =
      null !== props.dndItemsList
        ? ADND.targets.itemsList.listItem({
            type,
            list: props.dndItemsList
          })
        : null
    this.dndListItemHandle =
      null !== props.dndItemsList
        ? ADND.sources
            .listItemHandle({
              type,
              item: this.dndListItem,
              cloneable: false,
              edgeScrolling: false
            })
            .onStart(this.dndStart.bind(this))
            .onEnd(this.dndEnd.bind(this))
            .fetchData(this.dndFetchData.bind(this))
        : null
  }

  dndStart() {
    const { model, listState, position, actions, saveReportTypeRef, saveReportPositionRef } = this.props
    updateListState({
      updates: [{ path: ['reorder'], value: true }],
      listState,
      actions
    })
    saveReportTypeRef(model.get('type'))
    saveReportPositionRef(position)
  }

  dndEnd() {
    const { listState, actions, saveReportTypeRef, saveReportPositionRef } = this.props
    updateListState({
      updates: [{ path: ['reorder'], value: null }],
      listState,
      actions
    })
    saveReportTypeRef(null)
    saveReportPositionRef(null)
  }

  dndFetchData() {
    return { model: this.props.model.toJS() }
  }

  saveListItemRef(ref) {
    this.saveRef('Component')(ref)
    this.connectDNDListItem(ref)
  }

  saveListItemHandleRef(ref) {
    this.saveRef('ComponentHandle')(ref)
    this.connectDNDListItemHandle(ref)
  }

  connectDNDListItem(ref) {
    null !== this.dndListItem && this.dndListItem.connect(ref)
  }

  connectDNDListItemHandle(ref) {
    null !== this.dndListItemHandle && this.dndListItemHandle.connect(ref)
  }

  onReportDelete() {
    const { model, listState, actions } = this.props
    updateListState({
      updates: [
        { path: reportStatePath(model.get('id'), 'active'), value: true },
        { path: reportStatePath(model.get('id'), 'mode'), value: 'delete' }
      ],
      listState,
      actions
    })
  }

  onReportSelect(event) {
    event.preventDefault()

    const { model, actions } = this.props
    actions.updateCurrentReportId(model.get('id'))
  }

  onReportView(event) {
    event.stopPropagation()

    const { model, actions } = this.props
    // Address the finished report before reloading: the load reconciles the list, but without this
    // it comes back on whatever the URL already held — for a user who never opened the report, the
    // Default Report rather than the one they clicked.
    actions.updateCurrentReportId(model.get('id'), () => window.location.reload())
  }

  checkOverflow() {
    if (!this.refTitle) return

    const div = document.createElement('div')

    document.body.appendChild(div)

    div.style.position = 'absolute'
    div.style.left = '-1000px'
    div.style.top = '-1000px'
    div.style.width = '194px'

    div.style.fontSize = window.getComputedStyle(this.refTitle, null).getPropertyValue('font-size') || '14px'
    div.style.lineHeight = window.getComputedStyle(this.refTitle, null).getPropertyValue('line-height') || '16px'

    div.style.overflow = 'hidden'
    div.style['overflow-wrap'] = 'break-word'

    div.innerHTML = this.refTitle.innerHTML
    this.setState({ overflow: 80 < div.clientHeight })

    div.parentNode.removeChild(div)
  }

  componentDidMount() {
    if (true === this.props.state.get('new')) {
      updateListState({
        updates: [{ path: reportStatePath(this.props.model.get('id'), 'new'), value: null }],
        listState: this.props.listState,
        actions: this.props.actions
      })

      this.refComponent.scrollIntoView(false)
    }

    this.checkOverflow()
  }

  componentDidUpdate(prevProps) {
    if (this.props.model.get('title') != prevProps.model.get('title')) {
      this.checkOverflow()
    }
    if (this.props.model.get('hidden') != prevProps.model.get('hidden')) {
      null !== this.dndListItem && this.dndListItem.disconnect()
      null !== this.dndListItemHandle && this.dndListItemHandle.disconnect()

      const type = dndType(this.props)

      this.dndListItem =
        null !== this.props.dndItemsList
          ? ADND.targets.itemsList.listItem({
              type,
              list: this.props.dndItemsList
            })
          : null
      this.dndListItemHandle =
        null !== this.props.dndItemsList
          ? ADND.sources
              .listItemHandle({
                type,
                item: this.dndListItem,
                cloneable: false,
                edgeScrolling: false
              })
              .onStart(this.dndStart.bind(this))
              .onEnd(this.dndEnd.bind(this))
              .fetchData(this.dndFetchData.bind(this))
          : null

      this.connectDNDListItem(this.refComponent)
      this.connectDNDListItemHandle(this.refComponentHandle)
    }
  }

  componentWillUnmount() {
    null !== this.dndListItem && this.dndListItem.disconnect()
    null !== this.dndListItemHandle && this.dndListItemHandle.disconnect()
  }

  renderTitle() {
    const { model } = this.props
    const { overflow } = this.state

    const title = model.get('title')

    // Rendered as text, not markup: the title arrives escaped, so decoding it here shows the
    // user's literal characters while markup inside a title stays inert.
    return (
      <span
        ref={this.saveRef('Title')}
        className={classNames(style['title-text'], { [style['title-text-overflow']]: overflow })}
      >
        {decodeHtmlEntities(
          'xpert' == model.get('type')
            ? 0 < title.length && 'xpert report' != title.trim().replace(/\s+/, ' ').toLowerCase()
              ? `Xpert Report: ${title}`
              : 'Xpert Report'
            : title
        )}
      </span>
    )
  }

  render() {
    const { state, model, features, virtualReportsIds, currentReportId, disabled, isSurveyGuest, isSurveyViewer } =
      this.props
    const isNew = true === state.get('new')
    const isDeleted = true === state.get('deleted')
    const isActive = true === state.get('active')
    const isCurrent = currentReportId == model.get('id')
    const isChanged = true === state.get('changed')
    const isDeleting = isActive && 'delete' == state.get('mode')
    // The row stays a spinner until the generation is ready even once it has adopted the
    // persisted report — mid-flight that body is the stat server's placeholder, not a report.
    const isGenerating = 'generating' == model.get('status')
    const isReady = 'ready' == model.get('status')
    const isDisabled = disabled
    const isLocked =
      ['original', 'xpert', 'widget', 'concept', ...virtualReportsIds].includes(model.get('type')) ||
      ('skipper' == model.get('type') && !isSkipperGuidedReport(model)) ||
      (features?.[SKIPPER_REPORT_FEATURE]?.disabled && isSkipperGuidedReport(model)) ||
      isDisabled ||
      isSurveyGuest ||
      isSurveyViewer

    return (
      <div
        ref={this.saveListItemRef || this.saveRef('Component')}
        className={classNames(style.component, style['component-' + model.get('type')], {
          [style['component-new']]: isNew,
          [style['component-deleted']]: isDeleted,
          [style['component-current']]: isCurrent,
          [style['component-active']]: isActive,
          [style['component-warning']]: isChanged,
          [style['component-error']]: isDeleting,
          [style['component-disabled']]: isDisabled
        })}
      >
        {isDisabled ? (
          <span className={style.title}>{this.renderTitle()}</span>
        ) : isGenerating ? (
          // Still generating: the row opens the stat server's placeholder body, not a report yet.
          <a
            href={`#charts/${model.get('id')}`}
            className={classNames(style.title, style.generating)}
            onClick={this.onReportSelect}
          >
            <i className="pi pi-spin pi-spinner" />
            {this.renderTitle()}
          </a>
        ) : isReady ? (
          // Generation finished — the user opens the finished report from here; the page
          // load is what serves it, there is no in-session swap.
          <a
            href={`#charts/${model.get('id')}`}
            className={classNames(style.title, style.ready)}
            onClick={this.onReportSelect}
          >
            {this.renderTitle()}
            <span className={style['ready-view']} onClick={this.onReportView}>
              View report
            </span>
          </a>
        ) : (
          <>
            {model.get('protected') || isLocked ? null : (
              <div className={style.delete} onClick={this.onReportDelete}>
                <i className="icon-aytmfd-delete" />
              </div>
            )}
            <a href={`#charts/${model.get('id')}`} className={style.title} onClick={this.onReportSelect}>
              {this.renderTitle()}
            </a>
          </>
        )}
        {model.get('hidden') ? (
          <span className={`${style.icon} ${style['icon-hidden']}`}>
            <i className="icon-aytmfd-preview" />
          </span>
        ) : null}
        <ReportPopup />
        {/*
          Hide the drag handle when:
            - the report is hidden and its type is 'skipper' or 'concept' (these types stay sortable while visible);
            - the report is virtual (never sortable);
            - the sidebar is disabled.
        */}
        {!(
          ['skipper', 'concept'].includes(model.get('type')) &&
          (['draft', 'generating', 'ready'].includes(model.get('status')) || model.get('hidden'))
        ) &&
        !virtualReportsIds.includes(model.get('type')) &&
        !isDisabled ? (
          <div ref={this.saveListItemHandleRef || this.saveRef('ComponentHandle')} className={style.handle}>
            <i className="icon-aytmfd-handle" />
          </div>
        ) : null}
      </div>
    )
  }
}

Report = ContextProvider({
  name: 'report',
  propTypes: {
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    position: PropTypes.number.isRequired
  },
  contextTypes: {
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    position: PropTypes.number.isRequired
  },
  values(props) {
    return {
      state: props.state,
      model: props.model,
      position: props.position
    }
  },
  shouldUpdate(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.position != prevProps.position
    )
  },
  mapToProps(values) {
    return {
      state: values.state,
      model: values.model,
      position: values.position
    }
  }
})(Report)

Report = ContextSubscriber({
  name: 'reportList',
  contextTypes: {
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    actions: PropTypes.object.isRequired,
    features: PropTypes.object,
    windowUID: PropTypes.string.isRequired,
    currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    virtualReportsIds: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    isSurveyGuest: PropTypes.bool,
    isSurveyViewer: PropTypes.bool
  },
  mapToProps(values) {
    return {
      listState: values.state,
      listModel: values.model,
      actions: values.actions,
      features: values.features,
      windowUID: values.windowUID,
      currentReportId: values.currentReportId,
      virtualReportsIds: values.virtualReportsIds,
      disabled: values.disabled,
      isSurveyGuest: values.isSurveyGuest,
      isSurveyViewer: values.isSurveyViewer
    }
  }
})(Report)

export default Report
